import {CustomEqualitySet} from "custom-equality-set";
import * as JSONMapper from "json-mapper";
import {JSONMapped, jsonProperty} from "json-mapper-reflect";
import {Money} from "money";
import {AvdbType} from "../avdb-type";
import {AvdbPricingDeviceBundleAvdbType} from "./avdb-pricing-device-bundle-avdb-type";
import {AvdbPricingGarminPilotProduct} from "./avdb-pricing-garmin-pilot-product";
import {AvdbPricingMapRegion, AvdbPricingMapRegionID} from "./avdb-pricing-map-region";

export abstract class AvdbPricingBundle extends JSONMapped() {
	@jsonProperty()
	partKey: string;

	@jsonProperty()
	description: string;

	@jsonProperty()
	customerComments: string;

	@jsonProperty(JSONMapper.classByMethods(Money))
	price: Money;

	@jsonProperty()
	bundleType: string;

	@jsonProperty()
	deviceID: number;

	@jsonProperty()
	addons: Array<number>;

	@jsonProperty()
	isAFR: boolean;

	@jsonProperty()
	isASI: boolean;

	@jsonProperty()
	isCAN: boolean;

	@jsonProperty()
	isCEN: boolean;

	@jsonProperty()
	isEUR: boolean;

	@jsonProperty()
	isGRE: boolean;

	@jsonProperty()
	isOCE: boolean;

	@jsonProperty()
	isNZ: boolean;

	@jsonProperty()
	isSAM: boolean;

	@jsonProperty()
	isUSA: boolean;

	@jsonProperty()
	fixedWingEligible: boolean;

	@jsonProperty()
	helicopterEligible: boolean;

	@jsonProperty(JSONMapper.arrayOfClass(AvdbPricingGarminPilotProduct))
	garminPilotProducts: Array<AvdbPricingGarminPilotProduct>;

	@jsonProperty()
	hasFliteCharts: boolean;

	constructor(data?: Partial<AvdbPricingBundle>) {
		super();
		Object.assign(this, data);
	}

	abstract readonly allAvdbTypes: CustomEqualitySet<AvdbType>;
	abstract avdbTypesForDeviceModelID(modelID: number): Array<AvdbPricingDeviceBundleAvdbType>;
	abstract equals(otherBundle: AvdbPricingBundle): boolean;

	static BUNDLE_TYPE_ONEPAK = "onepak";
	static BUNDLE_TYPE_PILOTPAK = "pilotpak";
	static BUNDLE_TYPE_DEVICE = "device";

	get bundleTypeDisplay(): string {
		switch (this.bundleType) {
			case AvdbPricingBundle.BUNDLE_TYPE_ONEPAK:
				return "OnePak";
			case AvdbPricingBundle.BUNDLE_TYPE_PILOTPAK:
				return "PilotPak";
			case AvdbPricingBundle.BUNDLE_TYPE_DEVICE:
				return "Database Bundle";
			default:
				return "Bundle";
		}
	}

	isMapRegionSupported(mapRegionID: AvdbPricingMapRegionID): boolean {
		switch (mapRegionID) {
			case AvdbPricingMapRegion.GREENLAND.id:
				return this.isGRE;
			case AvdbPricingMapRegion.CANADA.id:
				return this.isCAN;
			case AvdbPricingMapRegion.UNITED_STATES.id:
				return this.isUSA;
			case AvdbPricingMapRegion.CENTRAL_AMERICA.id:
				return this.isCEN;
			case AvdbPricingMapRegion.SOUTH_AMERICA.id:
				return this.isSAM;
			case AvdbPricingMapRegion.EUROPE.id:
				return this.isEUR;
			case AvdbPricingMapRegion.AFRICA.id:
				return this.isAFR;
			case AvdbPricingMapRegion.ASIA.id:
				return this.isASI;
			case AvdbPricingMapRegion.OCEANIA.id:
				return this.isOCE;
			case AvdbPricingMapRegion.NEW_ZEALAND.id:
				return this.isNZ;
			default:
				return false;
		}
	}

	abstract get productOptions(): Array<string>;
}
